import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { NAMESPACES } from "@/i18n/constants";
import { ICON_KEYS, iconRegistry } from "@/icons";
import { cn } from "@/utils/class-name";
import { SpecContext } from "../spec/spec-context";
import { groupDisplayedStreams } from "./group-picker-streams";
import { type PickerRowText, pickerRowTexts } from "./picker-row-text";
import { GroupSection, ROW_CLASS, RowText, StatusLine, usePickerLabels, useRowLabel } from "./picker-rows";
import type { PickerStream } from "./picker-stream";

const RemoveIcon = iconRegistry[ICON_KEYS.CLOSE];

export function DisplayedTab({
    plotted,
    addedIds,
    onRemove,
}: {
    plotted: readonly PickerStream[];
    addedIds: ReadonlySet<number>;
    onRemove: (streamId: number) => void;
}) {
    const { t: tMessages } = useTranslation(NAMESPACES.FEATURES.DATA_VIEWER, { keyPrefix: "messages" });
    const labels = usePickerLabels();
    const showVisibility = plotted.some((stream) => stream.visibility !== "shown");
    const colors = useSpecColors();

    const { zoneStreams, addedByZone } = useMemo(
        () => groupDisplayedStreams(plotted, addedIds, labels),
        [plotted, addedIds, labels],
    );

    return (
        <>
            {addedByZone.map((group) => (
                <DisplayedGroup
                    key={group.key}
                    label={group.label}
                    streams={group.streams}
                    addedIds={addedIds}
                    colors={colors}
                    onRemove={onRemove}
                    showVisibility={showVisibility}
                />
            ))}
            <DisplayedGroup
                label={labels.thisZone}
                streams={zoneStreams}
                addedIds={addedIds}
                colors={colors}
                onRemove={onRemove}
                showVisibility={showVisibility}
                emptyText={tMessages("empty")}
            />
        </>
    );
}

/**
 * The spec's colors by stream id, so a swatch matches its line: a stream's color is its place on the
 * card, which nothing about the stream itself says.
 */
function useSpecColors(): ReadonlyMap<number, string> {
    const spec = useContext(SpecContext)?.spec;
    return useMemo(
        () =>
            new Map(
                (spec?.panels ?? []).flatMap((panel) =>
                    panel.streams.map((entry) => [entry.streamId, entry.color] as const),
                ),
            ),
        [spec],
    );
}

function DisplayedGroup({
    label,
    streams,
    addedIds,
    colors,
    onRemove,
    showVisibility,
    emptyText,
}: {
    label: string;
    streams: readonly PickerStream[];
    addedIds: ReadonlySet<number>;
    colors: ReadonlyMap<number, string>;
    onRemove: (streamId: number) => void;
    showVisibility: boolean;
    emptyText?: string;
}) {
    const texts = pickerRowTexts(streams);
    return (
        <GroupSection label={label} count={streams.length} belowBorder>
            {streams.length === 0 && emptyText && <StatusLine>{emptyText}</StatusLine>}
            {streams.map((stream) => (
                <DisplayedRow
                    key={stream.id}
                    stream={stream}
                    color={colors.get(stream.id)}
                    text={texts.get(stream.id) ?? { measurement: stream.name, detail: "" }}
                    group={label}
                    onRemove={addedIds.has(stream.id) ? onRemove : undefined}
                    showVisibility={showVisibility}
                />
            ))}
        </GroupSection>
    );
}

function DisplayedRow({
    stream,
    color,
    text,
    group,
    onRemove,
    showVisibility,
}: {
    stream: PickerStream;
    /**
     * The chart's color for the stream. Absent outside a viewer, or for the frame before the spec
     * catches up; the swatch goes gray then, as any palette color would be another line's.
     */
    color?: string;
    text: PickerRowText;
    group: string;
    onRemove?: (streamId: number) => void;
    showVisibility: boolean;
}) {
    const rowLabel = useRowLabel();
    return (
        <div className={ROW_CLASS}>
            <span
                aria-hidden
                className={cn("h-[4px] w-[14px] shrink-0 rounded-[2px]", !color && "bg-muted-foreground/30")}
                style={color ? { backgroundColor: color } : undefined}
            />
            <RowText stream={stream} text={text} showVisibility={showVisibility} />
            {onRemove && (
                <Button
                    aria-label={rowLabel("remove", stream, text, group)}
                    onClick={() => onRemove(stream.id)}
                    size={"icon-xs"}
                    variant={"outline"}
                >
                    <RemoveIcon />
                </Button>
            )}
        </div>
    );
}
